import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';

import {
  CompletenessLevel,
  InsertionSource,
  PerformanceLevel,
  type SearchFilterData,
} from '@/models/insertionsParameters';
import { InsertionStatus } from '@/types';

const STATUS_ALLOW = new Set(Object.values(InsertionStatus));
const OFFER_TYPES_ALLOW = new Set(Object.values(OfferType));
const RANK_TYPES_ALLOW = new Set(Object.values(RankType));
const SOURCE_ALLOW = new Set(Object.values(InsertionSource));
const COMPLETENESS_ALLOW = new Set(Object.values(CompletenessLevel));
const PERFORMANCE_ALLOW = new Set(Object.values(PerformanceLevel));

/**
 * Search in the object whether key exists.
 * Alternatively, the default value or an empty array is returned.
 *
 * @param key - key
 * @param queryParams - queryParams
 * @param searchFilterDefaults - searchFilterDefaults
 */
const getQueryParam = (
  key: string,
  queryParams: SearchFilterData,
  searchFilterDefaults: SearchFilterData,
): any[] => {
  const params: any = key in queryParams ? queryParams[key] : null;
  return params || searchFilterDefaults[key] || [];
};

/**
 * This function searches in the URLSearchParams for "searchFilter" (string in JSON format).
 * The values are partially compared with allowed values.
 * In case of illegal or incorrect values the predefined default value is returned.
 */
export default function searchFilterQueryDataValidation(
  searchFilterDefaults: SearchFilterData,
): SearchFilterData {
  const searchParams = new URLSearchParams(window.location.search);
  const searchFilter = searchParams.get('searchFilter');

  let queryParams = {};
  try {
    queryParams = searchFilter ? JSON.parse(searchFilter) : {};
  } catch (_) {
    // Silence error
  }

  return {
    category: getQueryParam('category', queryParams, searchFilterDefaults),
    searchQuery: getQueryParam('searchQuery', queryParams, searchFilterDefaults),
    status: getQueryParam('status', queryParams, searchFilterDefaults).filter((item) =>
      STATUS_ALLOW.has(item),
    ),
    offerType: getQueryParam('offerType', queryParams, searchFilterDefaults).filter((item) =>
      OFFER_TYPES_ALLOW.has(item),
    ),
    rankType: getQueryParam('rankType', queryParams, searchFilterDefaults).filter((item) =>
      RANK_TYPES_ALLOW.has(item),
    ),
    source: getQueryParam('source', queryParams, searchFilterDefaults).filter((item) =>
      SOURCE_ALLOW.has(item),
    ),
    completeness: getQueryParam('completeness', queryParams, searchFilterDefaults).filter((item) =>
      COMPLETENESS_ALLOW.has(item),
    ),
    performance: getQueryParam('performance', queryParams, searchFilterDefaults).filter((item) =>
      PERFORMANCE_ALLOW.has(item),
    ),
  };
}
